export function useUrl() {
	const config = useAppConfig();
	const nuxtApp = useNuxtApp();
	const {getInfo} = useInfo();

	// remove domain/host name from url
	function relative(url, options = {}) {
		if (!url) {
			useLog('Missing url property', 'error');
			return;
		}

		if (options?.onlyCurrentHost) {
			// Only remove domain if it matches the config host
			const linkUrl = new URL(url);
			const hostUrl = new URL(nuxtApp.$host);

			if (linkUrl.hostname === hostUrl.hostname) {
				return url.replace(/^.*\/\/[^\/]+:?[0-9]?/i, '');
			}
			return url;
		}

		return url.replace(/^.*\/\/[^\/]+:?[0-9]?/i, '');
	}

	// prepend domain/host name to url if not already available
	function absolute(url) {
		if (!url) {
			useLog('Missing url property', 'error');
			return;
		}
		const pattern = /^.*\/\/[^\/]+:?[0-9]?/i;
		return pattern.test(url) ? url : nuxtApp.$host + url;
	}

	// remove leading and trailing slash
	function stripSlashes(url) {
		if (!url) {
			useLog('Missing url property', 'error');
			return;
		}
		url = String(url).trim();
		if (url.startsWith('/')) {
			url = url.substring(1);
		}
		if (url.endsWith('/')) {
			url = url.substring(0, url.length - 1);
		}
		return url;
	}

	// extract query params from url and return as array
	function getUrlParams(url, options = {}) {
		const queryPart = url.split('?')[1];
		if (!queryPart) return [];

		const params = queryPart.split('&').map(param => {
			const [key, value] = param.split('=');
			return {key, value};
		});

		if (options?.mode == 'keys') {
			let keys = params.map(param => param.key);
			if (options.removeDuplicates) {
				keys = [...new Set(keys)];
			}
			return keys;
		}
		if (options?.mode == 'values') {
			let values = params.map(param => param.value);
			if (options.removeDuplicates) {
				values = [...new Set(values)];
			}
			return values;
		}

		return queryPart.split('&').map(param => {
			const [key, value] = param.split('=');
			return {[key]: decodeURIComponent(value)};
		});
	}

	// return array of url segments
	function getUrlSegments(url, options) {
		if (!url || typeof url != 'string') {
			if (import.meta.client) console.error('getUrlSegments: missing url or url is not a string!');
			return null;
		}

		let output = url.split('/');
		output = output?.length ? output.filter(element => element !== undefined && element !== null && element !== '') : [];

		// ignore language segment
		const langs = ['hr', 'en', 'de', 'at', 'rs', 'si', 'it', 'ba', 'me', 'es'];
		if (config?.multilanguage && options?.ignoreLang && langs.includes(output[0])) {
			output.shift();
		}

		// return only lang segment
		if (config?.multilanguage && options?.onlyLang && langs.includes(output[0])) {
			output = [output[0]];
		}

		// remove last segment if removeLast option is set
		if (options?.removeLast) {
			output.pop();
		}

		// if reverse option is set, reverse array
		if (options?.reverse) {
			output.reverse();
		}

		if (options?.offset && output.length) {
			output.splice(0, options.offset);
		}

		if (options?.limit && output.length) {
			output = output.slice(0, options.limit);
		}

		// if reverse option is set, reverse back
		if (options?.reverse) {
			output.reverse();
		}

		if (options?.returnLast && output.length) {
			output = [output[output.length - 1]];
		}

		if (options?.append) {
			output.push(options.append);
		}

		if (options?.stringify) {
			output = output.join('/');
		}

		if (options?.addSlashes) {
			output = '/' + output + '/';
		}

		return output == '//' ? '/' : output;
	}

	function getLastUrlSegment(url) {
		return getUrlSegments(url, {reverse: true, limit: 1, stringify: true});
	}

	function stripLangFromUrl(url) {
		return getUrlSegments(url, {ignoreLang: true, stringify: true, addSlashes: true});
	}

	function homeUrl() {
		const {getLang} = useLang();
		const currLanguage = getLang();
		if (config.multiHost?.length) return '/';
		return config.lang == currLanguage.code ? '/' : currLanguage.base_url;
	}

	function getLanguageUrls(data) {
		if (!data) return [];
		let urls = data.languages_urls;
		const allLangs = getInfo('languages');

		// Get language urls from different data structure if available
		if (!urls) {
			urls = {};
			allLangs.forEach(lang => {
				if (data['url_' + lang.code]) urls[lang.code] = data['url_' + lang.code];
			});
		}

		if (!urls) return [];

		const langUrls = Object.entries(urls).map(([key, value]) => {
			return {
				lang: key,
				url: value,
			};
		});

		// Handle languages that share a language code but have different country codes
		// (e.g. Austria: country_code='at', code='de' and Germany: country_code='de', code='de')

		// Group languages by code to detect shared-code groups
		const langGroups = {};
		allLangs.forEach(lang => {
			if (!langGroups[lang.code]) langGroups[lang.code] = [];
			langGroups[lang.code].push(lang);
		});

		// Process groups where multiple countries share the same language code
		Object.entries(langGroups).forEach(([code, group]) => {
			if (group.length <= 1) return;

			const baseEntry = langUrls.find(el => el.lang === code);
			if (!baseEntry) return;

			const {langs} = useLang();

			group.forEach(member => {
				let url = baseEntry.url;

				// For multihost: replace domain with this member's base_url when they differ.
				// This ensures each country gets its own domain URL regardless of which host is active.
				if (config.multiHost?.length && member.base_url) {
					try {
						const urlObj = new URL(baseEntry.url);
						const memberBaseObj = new URL(member.base_url);
						if (urlObj.hostname !== memberBaseObj.hostname) {
							url = memberBaseObj.origin + urlObj.pathname;
						}
					} catch (e) {
						// Non-absolute URLs: reuse as-is
					}
				}

				// Use locale from useLang as the BCP 47 hreflang key (e.g. de-AT, de-DE)
				const hreflang = langs[member.country_code]?.locale || member.code + '-' + member.country_code.toUpperCase();

				// Add a new entry or update the existing one for this member's hreflang key
				const existing = langUrls.find(el => el.lang === hreflang || el.lang === member.country_code);
				if (existing) {
					existing.lang = hreflang;
					existing.url = url;
				} else {
					langUrls.push({lang: hreflang, url});
				}
			});
		});

		// Filter out empty urls
		return langUrls.filter(d => d.url);
	}

	return {relative, absolute, stripSlashes, getUrlParams, getLastUrlSegment, getUrlSegments, stripLangFromUrl, homeUrl, getLanguageUrls};
}
